import type { HabitType, ScheduleType, TimeOfDay } from "../db/schema";

export interface HabitTemplate {
  id: string;
  name: string;
  icon: string;
  category: string;
  type: HabitType;
  scheduleType: ScheduleType;
  scheduleValue: string;
  targetCount: number;
  timeOfDay: TimeOfDay;
  description: string;
}

export const TEMPLATES: HabitTemplate[] = [
  // Health & Fitness
  {
    id: "t-workout",
    name: "Workout",
    icon: "\uD83C\uDFCB\uFE0F",
    category: "Health & Fitness",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":4}',
    targetCount: 1,
    timeOfDay: "morning",
    description: "Build strength and endurance",
  },
  {
    id: "t-run",
    name: "Go for a run",
    icon: "\uD83C\uDFC3",
    category: "Health & Fitness",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "morning",
    description: "Improve cardiovascular health",
  },
  {
    id: "t-water",
    name: "Drink water",
    icon: "\uD83D\uDCA7",
    category: "Health & Fitness",
    type: "count",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 8,
    timeOfDay: "anytime",
    description: "Stay hydrated throughout the day",
  },
  {
    id: "t-steps",
    name: "10,000 steps",
    icon: "\uD83D\uDC63",
    category: "Health & Fitness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Keep moving every day",
  },
  {
    id: "t-stretch",
    name: "Stretch",
    icon: "\uD83E\uDDD8",
    category: "Health & Fitness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Improve flexibility and reduce tension",
  },
  {
    id: "t-vitamins",
    name: "Take vitamins",
    icon: "\uD83D\uDC8A",
    category: "Health & Fitness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Support your health with daily supplements",
  },
  {
    id: "t-sleep-early",
    name: "Sleep by 11pm",
    icon: "\uD83C\uDF19",
    category: "Health & Fitness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Get enough rest",
  },
  {
    id: "t-no-sugar",
    name: "No added sugar",
    icon: "\uD83D\uDEAB",
    category: "Health & Fitness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Reduce sugar intake",
  },
  {
    id: "t-cook",
    name: "Cook a meal",
    icon: "\uD83C\uDF73",
    category: "Health & Fitness",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":5}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Eat healthier homemade food",
  },
  {
    id: "t-posture",
    name: "Posture check",
    icon: "\uD83E\uDDBF",
    category: "Health & Fitness",
    type: "count",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 3,
    timeOfDay: "anytime",
    description: "Check and correct your posture",
  },

  // Mindfulness
  {
    id: "t-meditate",
    name: "Meditate",
    icon: "\uD83E\uDDD8",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Start the day with clarity",
  },
  {
    id: "t-journal",
    name: "Journal",
    icon: "\uD83D\uDCD3",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Reflect on your day",
  },
  {
    id: "t-gratitude",
    name: "Gratitude",
    icon: "\uD83D\uDE4F",
    category: "Mindfulness",
    type: "count",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 3,
    timeOfDay: "evening",
    description: "Note three things you're grateful for",
  },
  {
    id: "t-breathe",
    name: "Breathing exercise",
    icon: "\uD83C\uDF2C\uFE0F",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Take a few minutes to breathe deeply",
  },
  {
    id: "t-no-phone-morning",
    name: "No phone first hour",
    icon: "\uD83D\uDCF5",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Start the day present",
  },
  {
    id: "t-digital-sunset",
    name: "Digital sunset",
    icon: "\uD83C\uDF05",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "No screens 1 hour before bed",
  },
  {
    id: "t-nature",
    name: "Time in nature",
    icon: "\uD83C\uDF33",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Connect with the outdoors",
  },
  {
    id: "t-affirmations",
    name: "Affirmations",
    icon: "\u2728",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Affirm your goals and values",
  },
  {
    id: "t-body-scan",
    name: "Body scan",
    icon: "\uD83E\uDDA0",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Notice how your body feels",
  },
  {
    id: "t-single-task",
    name: "Single-task for 1 hour",
    icon: "\uD83C\uDFAF",
    category: "Mindfulness",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Focus deeply on one thing",
  },

  // Learning
  {
    id: "t-read",
    name: "Read",
    icon: "\uD83D\uDCDA",
    category: "Learning",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Read for at least 20 minutes",
  },
  {
    id: "t-flashcards",
    name: "Study flashcards",
    icon: "\uD83C\uDCCF",
    category: "Learning",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Review your spaced repetition cards",
  },
  {
    id: "t-language",
    name: "Language practice",
    icon: "\uD83C\uDF0D",
    category: "Learning",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Practice a new language",
  },
  {
    id: "t-podcast",
    name: "Educational podcast",
    icon: "\uD83C\uDFA7",
    category: "Learning",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Learn something new while commuting",
  },
  {
    id: "t-online-course",
    name: "Online course",
    icon: "\uD83C\uDF93",
    category: "Learning",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Make progress on your course",
  },
  {
    id: "t-write",
    name: "Write 500 words",
    icon: "\u270D\uFE0F",
    category: "Learning",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Build a daily writing habit",
  },
  {
    id: "t-vocab",
    name: "Learn new words",
    icon: "\uD83D\uDCD6",
    category: "Learning",
    type: "count",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 5,
    timeOfDay: "morning",
    description: "Expand your vocabulary",
  },
  {
    id: "t-chess",
    name: "Chess puzzle",
    icon: "\u265F\uFE0F",
    category: "Learning",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Sharpen your mind with tactics",
  },
  {
    id: "t-instrument",
    name: "Practice instrument",
    icon: "\uD83C\uDFB5",
    category: "Learning",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Build musical skill",
  },
  {
    id: "t-news",
    name: "Read the news",
    icon: "\uD83D\uDCF0",
    category: "Learning",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Stay informed",
  },

  // Productivity
  {
    id: "t-plan-day",
    name: "Plan the day",
    icon: "\uD83D\uDCCB",
    category: "Productivity",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Set intentions before you start",
  },
  {
    id: "t-review-day",
    name: "Review the day",
    icon: "\uD83D\uDD0D",
    category: "Productivity",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Reflect on what went well",
  },
  {
    id: "t-deep-work",
    name: "Deep work session",
    icon: "\uD83E\uDDE0",
    category: "Productivity",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Focus on cognitively demanding work",
  },
  {
    id: "t-inbox-zero",
    name: "Process inbox",
    icon: "\uD83D\uDCE7",
    category: "Productivity",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Keep your inbox manageable",
  },
  {
    id: "t-pomodoro",
    name: "Pomodoro sessions",
    icon: "\uD83C\uDF45",
    category: "Productivity",
    type: "count",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 4,
    timeOfDay: "morning",
    description: "Focused 25-minute work blocks",
  },
  {
    id: "t-no-social",
    name: "No social media",
    icon: "\uD83D\uDCF1",
    category: "Productivity",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Reclaim your attention",
  },
  {
    id: "t-tidy-desk",
    name: "Tidy desk",
    icon: "\uD83E\uDDF9",
    category: "Productivity",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "End the day with a clean workspace",
  },
  {
    id: "t-weekly-review",
    name: "Weekly review",
    icon: "\uD83D\uDDD3\uFE0F",
    category: "Productivity",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Review goals and priorities",
  },
  {
    id: "t-most-important",
    name: "Most important task",
    icon: "\uD83C\uDFAF",
    category: "Productivity",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Do the most impactful thing first",
  },
  {
    id: "t-batch-tasks",
    name: "Batch similar tasks",
    icon: "\uD83D\uDCE6",
    category: "Productivity",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Group similar tasks together",
  },

  // Self-Care
  {
    id: "t-skincare",
    name: "Skincare routine",
    icon: "\u2728",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Take care of your skin",
  },
  {
    id: "t-floss",
    name: "Floss",
    icon: "\uD83E\uDDB7",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Dental hygiene matters",
  },
  {
    id: "t-sunscreen",
    name: "Apply sunscreen",
    icon: "\u2600\uFE0F",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Protect your skin",
  },
  {
    id: "t-hobby",
    name: "Hobby time",
    icon: "\uD83C\uDFA8",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Do something just for fun",
  },
  {
    id: "t-no-caffeine-pm",
    name: "No caffeine after 2pm",
    icon: "\u2615",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Protect your sleep quality",
  },
  {
    id: "t-laugh",
    name: "Laugh today",
    icon: "\uD83D\uDE04",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Find joy in the day",
  },
  {
    id: "t-self-date",
    name: "Solo outing",
    icon: "\uD83C\uDF1F",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Enjoy your own company",
  },
  {
    id: "t-massage",
    name: "Self-massage",
    icon: "\uD83D\uDC86",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":2}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Release muscle tension",
  },
  {
    id: "t-cold-shower",
    name: "Cold shower",
    icon: "\uD83E\uDEE7",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "morning",
    description: "Boost alertness and recovery",
  },
  {
    id: "t-nap",
    name: "Power nap",
    icon: "\uD83D\uDE34",
    category: "Self-Care",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Recharge with a short nap",
  },

  // Social
  {
    id: "t-call-friend",
    name: "Call a friend",
    icon: "\uD83D\uDCDE",
    category: "Social",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":2}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Maintain your relationships",
  },
  {
    id: "t-call-family",
    name: "Call family",
    icon: "\uD83D\uDC68\u200D\uD83D\uDC69\u200D\uD83D\uDC67\u200D\uD83D\uDC66",
    category: "Social",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":2}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Stay connected with loved ones",
  },
  {
    id: "t-compliment",
    name: "Give a compliment",
    icon: "\uD83D\uDCAC",
    category: "Social",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Brighten someone's day",
  },
  {
    id: "t-random-kindness",
    name: "Act of kindness",
    icon: "\uD83D\uDC9A",
    category: "Social",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Do something kind for someone",
  },
  {
    id: "t-social-event",
    name: "Social event",
    icon: "\uD83C\uDF89",
    category: "Social",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Get out and socialize",
  },
  {
    id: "t-listen",
    name: "Active listening",
    icon: "\uD83D\uDC42",
    category: "Social",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Give someone your full attention",
  },
  {
    id: "t-thank-someone",
    name: "Thank someone",
    icon: "\uD83D\uDE4F",
    category: "Social",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Express genuine gratitude",
  },
  {
    id: "t-mentor",
    name: "Mentor session",
    icon: "\uD83E\uDDD1\u200D\uD83C\uDFEB",
    category: "Social",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Share knowledge with someone",
  },
  {
    id: "t-volunteer",
    name: "Volunteer",
    icon: "\uD83E\uDD1D",
    category: "Social",
    type: "boolean",
    scheduleType: "x_per_month",
    scheduleValue: '{"count":2}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Give back to your community",
  },
  {
    id: "t-new-person",
    name: "Talk to someone new",
    icon: "\uD83D\uDC4B",
    category: "Social",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Expand your social circle",
  },

  // Creative
  {
    id: "t-draw",
    name: "Draw or sketch",
    icon: "\u270F\uFE0F",
    category: "Creative",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Express yourself visually",
  },
  {
    id: "t-photo",
    name: "Take a photo",
    icon: "\uD83D\uDCF7",
    category: "Creative",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Capture something interesting",
  },
  {
    id: "t-creative-writing",
    name: "Creative writing",
    icon: "\uD83D\uDD8A\uFE0F",
    category: "Creative",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Write fiction, poetry, or stories",
  },
  {
    id: "t-doodle",
    name: "Doodle",
    icon: "\uD83C\uDFA8",
    category: "Creative",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Let your pen wander",
  },
  {
    id: "t-brainstorm",
    name: "Brainstorm ideas",
    icon: "\uD83D\uDCA1",
    category: "Creative",
    type: "count",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 10,
    timeOfDay: "morning",
    description: "Generate ideas freely",
  },
  {
    id: "t-cook-new",
    name: "Try a new recipe",
    icon: "\uD83D\uDC68\u200D\uD83C\uDF73",
    category: "Creative",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Explore new flavors",
  },
  {
    id: "t-music-create",
    name: "Make music",
    icon: "\uD83C\uDFB6",
    category: "Creative",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Create something musical",
  },
  {
    id: "t-craft",
    name: "Crafting time",
    icon: "\uD83E\uDDF6",
    category: "Creative",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":2}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Work on a craft project",
  },
  {
    id: "t-improv",
    name: "Improvise something",
    icon: "\uD83C\uDFAD",
    category: "Creative",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":2}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Practice spontaneous creativity",
  },
  {
    id: "t-design",
    name: "Design something",
    icon: "\uD83D\uDD8C\uFE0F",
    category: "Creative",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":2}',
    targetCount: 1,
    timeOfDay: "afternoon",
    description: "Explore visual design",
  },

  // Finance
  {
    id: "t-budget-review",
    name: "Review spending",
    icon: "\uD83D\uDCB3",
    category: "Finance",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Track your daily expenses",
  },
  {
    id: "t-save",
    name: "Save money",
    icon: "\uD83D\uDCB0",
    category: "Finance",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Transfer to savings",
  },
  {
    id: "t-no-impulse",
    name: "No impulse purchases",
    icon: "\uD83D\uDED2",
    category: "Finance",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Think before you buy",
  },
  {
    id: "t-financial-read",
    name: "Financial reading",
    icon: "\uD83D\uDCCA",
    category: "Finance",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":2}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Learn about personal finance",
  },
  {
    id: "t-invest-check",
    name: "Check investments",
    icon: "\uD83D\uDCC8",
    category: "Finance",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "morning",
    description: "Monitor your portfolio",
  },
  {
    id: "t-no-eating-out",
    name: "No eating out",
    icon: "\uD83C\uDF7D\uFE0F",
    category: "Finance",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":5}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Save by cooking at home",
  },
  {
    id: "t-expense-log",
    name: "Log expenses",
    icon: "\uD83D\uDCDD",
    category: "Finance",
    type: "boolean",
    scheduleType: "daily",
    scheduleValue: "{}",
    targetCount: 1,
    timeOfDay: "evening",
    description: "Record every transaction",
  },
  {
    id: "t-side-hustle",
    name: "Side project work",
    icon: "\uD83D\uDE80",
    category: "Finance",
    type: "boolean",
    scheduleType: "x_per_week",
    scheduleValue: '{"count":3}',
    targetCount: 1,
    timeOfDay: "evening",
    description: "Build additional income streams",
  },
  {
    id: "t-debt-payment",
    name: "Extra debt payment",
    icon: "\uD83D\uDCB8",
    category: "Finance",
    type: "boolean",
    scheduleType: "x_per_month",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Pay down debt faster",
  },
  {
    id: "t-negotiate",
    name: "Negotiate or compare",
    icon: "\uD83E\uDD1D",
    category: "Finance",
    type: "boolean",
    scheduleType: "x_per_month",
    scheduleValue: '{"count":1}',
    targetCount: 1,
    timeOfDay: "anytime",
    description: "Find better deals on recurring costs",
  },
];

export function getTemplatesByCategory(): Map<string, HabitTemplate[]> {
  const map = new Map<string, HabitTemplate[]>();
  for (const template of TEMPLATES) {
    const list = map.get(template.category);
    if (list) {
      list.push(template);
    } else {
      map.set(template.category, [template]);
    }
  }
  return map;
}
